import { useState, useCallback, useRef } from 'react';
import { placesApiService, PlacePrediction, PlaceDetails } from '../services/placesApi';

interface UsePlacesSearchReturn {
  predictions: PlacePrediction[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
  selectPlace: (prediction: PlacePrediction) => Promise<PlaceDetails | null>;
  clearPredictions: () => void;
}

export const usePlacesSearch = (): UsePlacesSearchReturn => {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const search = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setPredictions([]);
      setError(null);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    setError(null);

    try {
      const results = await placesApiService.searchPlaces(query, ['us', 'ca', 'gb', 'au']);
      setPredictions(results);
    } catch (err) {
      if (err instanceof Error && err.name !== 'AbortError') {
        // console.error('❌ Error searching places:', err);
        setError(err.message);
        setPredictions([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const selectPlace = useCallback(async (prediction: PlacePrediction): Promise<PlaceDetails | null> => {
    try {
      setIsLoading(true);
      setError(null);
      
      const details = await placesApiService.getPlaceDetails(prediction.place_id);
      setPredictions([]);
      
      return details;
    } catch (err) {
      // console.error('❌ Error getting place details:', err);
      setError(err instanceof Error ? err.message : 'Failed to get place details');
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const clearPredictions = useCallback(() => {
    setPredictions([]);
    setError(null);
  }, []);

  return {
    predictions,
    isLoading,
    error,
    search,
    selectPlace,
    clearPredictions,
  };
};
